import React, { useState, useEffect } from 'react';

import UserApi from 'entities/user/api';
import {
  currencies,
  currencyFormat,
  currenciesFormat,
  CurrencyCode,
  AmountCurrencies,
} from 'utils/currencyFormat';

export interface CurrencyData {
  currencyCode: CurrencyCode;
  rates: {
    [sourceCurrencyCode: string]: {
      [targetCurrencyCode: string]: number;
    };
  };
}

interface RatesCache {
  [cacheKey: string]: Promise<CurrencyData>;
}

const ratesCache: RatesCache = {};

const fetchRates = (): Promise<CurrencyData> => {
  const cacheKey = `${new Date().toISOString().slice(0, 10)}`;
  if (cacheKey in ratesCache) {
    return ratesCache[cacheKey];
  }
  const fetchPromise = UserApi.myCurrency().toPromise() as Promise<CurrencyData>;
  ratesCache[cacheKey] = fetchPromise;
  return fetchPromise;
};

export const useCurrency = (passedCurrencyCode?: CurrencyCode) => {
  const [currencyData, setExchangeRates] = useState<null | CurrencyData>(null);

  useEffect(() => {
    let isCancelled = false;
    fetchRates()
      .then((data) => {
        if (!isCancelled) {
          setExchangeRates(data);
        }
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
    return () => {
      isCancelled = true;
    };
  }, []);

  const currencyCode: CurrencyCode = passedCurrencyCode || currencyData?.currencyCode || 'USD';
  const currency = currencies[currencyCode];
  if (!currencyData) {
    return currency;
  }

  const ratesForCurrency: AmountCurrencies = {};
  Object.keys(currencyData?.rates || {}).forEach((sourceCurrencyCode) => {
    ratesForCurrency[sourceCurrencyCode] =
      currencyData?.rates?.[sourceCurrencyCode][currencyCode] || 1;
  });

  currency.rates = ratesForCurrency;

  return currency;
};

interface CurrencyFormatProps {
  // `amount` and `baseCurrency` must be set together
  // Used for live amounts that are based on today's rate
  amount?: number;
  baseCurrency?: CurrencyCode;
  targetCurrency: CurrencyCode;

  // Used for pre-converted amounts on the server-side
  amountCurrencies?: AmountCurrencies;
  short?: boolean;
}

export const CurrencyFormat = ({
  amount,
  baseCurrency,
  targetCurrency,
  amountCurrencies,
  short = false,
}: CurrencyFormatProps): JSX.Element => {
  const currency = useCurrency(targetCurrency);
  if (Number.isFinite(amount) && baseCurrency) {
    const rate = currency.rates?.[baseCurrency];
    if (!rate || amount === undefined) {
      return <></>;
    }
    return <>{currencyFormat(amount * rate, currency.code, short)}</>;
  }
  if (amountCurrencies) {
    return <>{currenciesFormat(amountCurrencies, currency.code, short)}</>;
  }
  return <></>;
};
